import { useMemo, useEffect, useRef } from 'react';
import { TextField } from '@/shared/components/TextField';
import { ProfilePictureUpload } from '@/shared/components/ProfilePictureUpload';
import FileUpload from '@/shared/components/FileUpload';
import { FileType } from '@/shared/components/FileUpload/constants';
import { useAddTechnician } from '../../hooks/useAddTechnician';
import CreateLayout from '@/shared/layout/createLayout';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import ErrorPage403 from '@/app/routes/errorPage403';
import Loader from '@/app/routes/Loader';
import type {
  AddTechnicianDetails,
  TechnicianPrivileges,
} from '../../types/addTechnicianType';
import { TECHNICIAN_LABELS } from '../../constants';
import PrivilegesSection from './PrivilegesSection';

const AddTechnicianHeader = () => (
  <p className="text-titleLarge text-neutral-10 ">
    {TECHNICIAN_LABELS.PAGE_TITLE}
  </p>
);

type AddTechnicianFormProps = {
  formData: AddTechnicianDetails;
  setFormData: React.Dispatch<React.SetStateAction<AddTechnicianDetails>>;
  errors: AddTechnicianDetails;
  isSubmitting: boolean;
  handleNameChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleEmailChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handlePhoneChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress1Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress2Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCityChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleStateChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleZipChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleProfileFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleProfileFileDelete: () => void;
  handleProfileFileError: (message: string) => void;
  handlePrivilegesChange: (privileges: TechnicianPrivileges) => void;
};

const AddTechnician = () => {
  const {
    snackbarError,
    isSnackbarOpen,
    handleCloseSnackbar,
    handleSubmit,
    handleCancel,
    isCancelModalOpen,
    handleCloseCancelModal,
    handleConfirmCancel,
    formData,
    setFormData,
    errors,
    submitErrorTrigger,
    isSubmitting,
    handleNameChange,
    handleEmailChange,
    handlePhoneChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
    handleProfileFileChange,
    handleProfileFileDelete,
    handleProfileFileError,
    handleDocFilesError,
    handlePrivilegesChange,
  } = useAddTechnician();

  const { privileges: callerPrivileges, isLoading: privilegesLoading } =
    useUserPrivileges();

  const errorsRef = useRef(errors);
  errorsRef.current = errors;

  useEffect(() => {
    if (submitErrorTrigger === 0) {
      return;
    }
    const fieldOrder: Array<[keyof typeof errorsRef.current, string]> = [
      ['name', TECHNICIAN_LABELS.FIELD_NAME],
      ['email', TECHNICIAN_LABELS.FIELD_EMAIL],
      ['phone', TECHNICIAN_LABELS.FIELD_PHONE],
      ['address1', TECHNICIAN_LABELS.FIELD_ADDRESS1],
      ['city', TECHNICIAN_LABELS.FIELD_CITY],
      ['state', TECHNICIAN_LABELS.FIELD_STATE],
      ['zip', TECHNICIAN_LABELS.FIELD_ZIP],
    ];
    const firstError = fieldOrder.find(([key]) =>
      Boolean(errorsRef.current[key])
    );
    if (firstError) {
      document
        .querySelector(`[data-testid="${firstError[1]}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submitErrorTrigger]);

  const AddTechnicianForm = useMemo(
    () =>
      function AddTechnicianForm({
        formData,
        setFormData,
        errors,
        isSubmitting,
        handleNameChange,
        handleEmailChange,
        handlePhoneChange,
        handleAddress1Change,
        handleAddress2Change,
        handleCityChange,
        handleStateChange,
        handleZipChange,
        handleProfileFileChange,
        handleProfileFileDelete,
        handleProfileFileError,
        handlePrivilegesChange,
      }: AddTechnicianFormProps) {
        return (
          <>
            {/* Basic Info */}
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                {TECHNICIAN_LABELS.SECTION_BASIC_INFO}
              </p>

              <ProfilePictureUpload
                onFileChange={handleProfileFileChange}
                onDelete={handleProfileFileDelete}
                onError={handleProfileFileError}
                disabled={isSubmitting}
                profileFile={formData.profileFile}
                initialLetter={
                  formData.name && formData.name.length > 0
                    ? formData.name
                        .trim()
                        .split(' ')
                        .slice(0, 2)
                        .map((word) => word.charAt(0).toUpperCase())
                        .join('')
                    : 'A'
                }
              />

              <div className="flex gap-4 ">
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_NAME}
                  dataTestId={TECHNICIAN_LABELS.FIELD_NAME}
                  autoTrimMaxLength={200}
                  value={formData.name}
                  onChange={handleNameChange}
                  error={Boolean(errors.name)}
                  errorMessage={errors.name}
                  disabled={isSubmitting}
                  autoFocus={true}
                />
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_EMAIL}
                  dataTestId={TECHNICIAN_LABELS.FIELD_EMAIL}
                  autoTrimMaxLength={100}
                  value={formData.email}
                  onChange={handleEmailChange}
                  error={Boolean(errors.email)}
                  errorMessage={errors.email}
                  disabled={isSubmitting}
                />
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_PHONE}
                  dataTestId={TECHNICIAN_LABELS.FIELD_PHONE}
                  type="tel"
                  maxLength={9}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  error={Boolean(errors.phone)}
                  errorMessage={errors.phone}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            {/* Address */}
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                {TECHNICIAN_LABELS.SECTION_ADDRESS}
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <TextField
                    fullWidth={true}
                    label={TECHNICIAN_LABELS.FIELD_ADDRESS1}
                    dataTestId={TECHNICIAN_LABELS.FIELD_ADDRESS1}
                    autoTrimMaxLength={200}
                    value={formData.address1}
                    onChange={handleAddress1Change}
                    error={Boolean(errors.address1)}
                    errorMessage={errors.address1}
                    disabled={isSubmitting}
                  />
                  <TextField
                    fullWidth={true}
                    label={
                      <>
                        {TECHNICIAN_LABELS.FIELD_ADDRESS2}
                        <span className="text-bodySmall font-normal">
                          {' '}
                          {TECHNICIAN_LABELS.FIELD_OPTIONAL}
                        </span>
                      </>
                    }
                    dataTestId={TECHNICIAN_LABELS.FIELD_ADDRESS2}
                    autoTrimMaxLength={200}
                    value={formData.address2}
                    onChange={handleAddress2Change}
                    disabled={isSubmitting}
                  />
                  <TextField
                    fullWidth={true}
                    label={TECHNICIAN_LABELS.FIELD_CITY}
                    dataTestId={TECHNICIAN_LABELS.FIELD_CITY}
                    autoTrimMaxLength={200}
                    value={formData.city}
                    onChange={handleCityChange}
                    error={Boolean(errors.city)}
                    errorMessage={errors.city}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex gap-4">
                  <TextField
                    fullWidth={true}
                    label={TECHNICIAN_LABELS.FIELD_STATE}
                    dataTestId={TECHNICIAN_LABELS.FIELD_STATE}
                    autoTrimMaxLength={200}
                    value={formData.state}
                    onChange={handleStateChange}
                    error={Boolean(errors.state)}
                    errorMessage={errors.state}
                    disabled={isSubmitting}
                  />
                  <TextField
                    fullWidth={true}
                    label={
                      <>
                        {TECHNICIAN_LABELS.FIELD_ZIP}
                        <span className="text-bodySmall font-normal">
                          {' '}
                          {TECHNICIAN_LABELS.FIELD_OPTIONAL}
                        </span>
                      </>
                    }
                    dataTestId={TECHNICIAN_LABELS.FIELD_ZIP}
                    maxLength={200}
                    value={formData.zip}
                    onChange={handleZipChange}
                    error={Boolean(errors.zip)}
                    errorMessage={errors.zip}
                    disabled={isSubmitting}
                  />
                  <TextField
                    fullWidth={true}
                    label={TECHNICIAN_LABELS.FIELD_COUNTRY}
                    dataTestId={TECHNICIAN_LABELS.FIELD_COUNTRY}
                    maxLength={200}
                    value={formData.country}
                    disabled={true}
                  />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            {/* Tax & Compliance Details */}
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                {TECHNICIAN_LABELS.SECTION_TAX_COMPLIANCE}
              </p>
              <div className="flex flex-col gap-2">
                <p className="text-[#474A4C]">
                  <span className="text-[16px] leading-[24px] tracking-[0.5px] font-normal">
                    {TECHNICIAN_LABELS.FIELD_GOVERNMENT_ID}
                  </span>
                  <span className="text-[12px] leading-[16px] tracking-[0.4px] font-normal">
                    {' '}
                    {TECHNICIAN_LABELS.FIELD_GOVERNMENT_ID_OPTIONAL}
                  </span>
                </p>
              </div>
              <FileUpload
                onFileSelect={(files) => {
                  setFormData((prev) => ({ ...prev, docFiles: files }));
                }}
                onError={handleDocFilesError}
                selectedFiles={formData.docFiles || []}
                allowedTypes={[
                  FileType.PNG,
                  FileType.JPG,
                  FileType.JPEG,
                  FileType.PDF,
                ]}
                maxFiles={2}
                disabled={isSubmitting}
              />
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            {/* Role & Permissions */}
            <div className="flex flex-col gap-4">
              <p className="text-[18px] text-on-surface-variant">
                {TECHNICIAN_LABELS.SECTION_ACCESS}
              </p>
              <div className="flex gap-4 w-[32%]">
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_ROLE}
                  dataTestId={TECHNICIAN_LABELS.FIELD_ROLE}
                  maxLength={200}
                  disabled={true}
                  value={formData.role}
                />
              </div>
              <PrivilegesSection
                privileges={formData.privileges}
                onPrivilegesChange={handlePrivilegesChange}
                disabled={isSubmitting}
              />
            </div>
          </>
        );
      },
    [handleDocFilesError]
  );

  if (privilegesLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-screen w-full">
        <Loader />
      </div>
    );
  }

  if (!callerPrivileges.technicianCreate) {
    return <ErrorPage403 />;
  }

  return (
    <CreateLayout
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      isCancelModalOpen={isCancelModalOpen}
      handleCloseCancelModal={handleCloseCancelModal}
      handleConfirmCancel={handleConfirmCancel}
      isSnackbarOpen={isSnackbarOpen}
      handleCloseSnackbar={handleCloseSnackbar}
      snackbarError={snackbarError}
      Header={AddTechnicianHeader}
      submitButtonLabel={TECHNICIAN_LABELS.SUBMIT_BUTTON}
      submitButtonLoadingLabel={TECHNICIAN_LABELS.SUBMIT_BUTTON_LOADING}
    >
      <AddTechnicianForm
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        isSubmitting={isSubmitting}
        handleNameChange={handleNameChange}
        handleEmailChange={handleEmailChange}
        handlePhoneChange={handlePhoneChange}
        handleAddress1Change={handleAddress1Change}
        handleAddress2Change={handleAddress2Change}
        handleCityChange={handleCityChange}
        handleStateChange={handleStateChange}
        handleZipChange={handleZipChange}
        handleProfileFileChange={handleProfileFileChange}
        handleProfileFileDelete={handleProfileFileDelete}
        handleProfileFileError={handleProfileFileError}
        handlePrivilegesChange={handlePrivilegesChange}
      />
    </CreateLayout>
  );
};

export default AddTechnician;
